import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Ship, Upload, FileSpreadsheet, CheckCircle, AlertCircle,
  Loader2, ArrowRight, Clock, Copy
} from 'lucide-react';

export default function MovementsPage() {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importHistory, setImportHistory] = useState([]);

  useEffect(() => {
    loadMovements();
    loadImportHistory();
  }, []);

  const loadMovements = async () => {
    try {
      const { data } = await api.get('/movements');
      setMovements(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadImportHistory = async () => {
    try {
      const { data } = await api.get('/movements/imports/history');
      setImportHistory(data);
    } catch (e) { console.error(e); }
  };

  const handleCSVImport = async () => {
    const fileInput = document.getElementById('csv-import-input');
    if (!fileInput?.files?.[0]) return;
    setImporting(true);
    setImportResult(null);
    try {
      const form = new FormData();
      form.append('file', fileInput.files[0]);
      const { data } = await api.post('/movements/import-csv', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(data);
      fileInput.value = '';
      await loadMovements();
      await loadImportHistory();
    } catch (e) {
      setImportResult({ success: false, error: e.response?.data?.detail || 'Import failed' });
    }
    finally { setImporting(false); }
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="movements-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-950">Movements</h1>
          <p className="text-sm text-slate-500 mt-1">Cargo movements and trade data management</p>
        </div>
        <Badge variant="outline" className="rounded-sm text-xs">{movements.length} movements</Badge>
      </div>

      <Tabs defaultValue="list">
        <TabsList className="rounded-sm h-9 bg-slate-100 p-0.5">
          <TabsTrigger value="list" className="rounded-sm text-xs" data-testid="tab-movement-list">
            <Ship className="h-3 w-3 mr-1" />Movement List
          </TabsTrigger>
          <TabsTrigger value="import" className="rounded-sm text-xs" data-testid="tab-csv-import">
            <FileSpreadsheet className="h-3 w-3 mr-1" />CSV Import
          </TabsTrigger>
        </TabsList>

        {/* Movement List */}
        <TabsContent value="list">
          <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-14" />)}</div>
              ) : movements.length === 0 ? (
                <div className="py-24 text-center">
                  <Ship className="h-12 w-12 mx-auto text-slate-200 stroke-[1px]" />
                  <p className="mt-4 text-sm text-slate-400">No movements loaded. Import a CSV to get started.</p>
                </div>
              ) : (
                <table className="w-full text-sm" data-testid="movements-table">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-3">Trade ID</th>
                      <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-3">Vessel</th>
                      <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-3">Route</th>
                      <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-3">Dates</th>
                      <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-3">Quantity</th>
                      <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-3">Commodity</th>
                      <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-3">Counterparty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movements.map(m => (
                      <tr key={m.id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`movement-row-${m.trade_id}`}>
                        <td className="px-5 py-3">
                          <span className="font-mono text-xs font-bold text-slate-900">{m.trade_id}</span>
                        </td>
                        <td className="px-5 py-3">
                          <p className="text-xs text-slate-800">{m.vessel_name}</p>
                          {m.vessel_imo && <p className="text-[10px] text-slate-400 font-mono">IMO: {m.vessel_imo}</p>}
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-1 text-xs text-slate-600">
                            <span>{m.port_load || '—'}</span>
                            <ArrowRight className="h-3 w-3 text-slate-300" />
                            <span>{m.port_discharge || '—'}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3">
                          <p className="text-xs font-mono text-slate-600">
                            {m.load_date ? new Date(m.load_date).toLocaleDateString() : '—'}
                          </p>
                          <p className="text-[10px] text-slate-400">
                            to {m.discharge_date ? new Date(m.discharge_date).toLocaleDateString() : '—'}
                          </p>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <span className="font-mono text-xs text-slate-900">
                            {m.cargo_quantity_mt ? parseFloat(m.cargo_quantity_mt).toLocaleString() : '—'}
                          </span>
                          <span className="text-[10px] text-slate-400 ml-1">MT</span>
                        </td>
                        <td className="px-5 py-3">
                          {m.commodity ? (
                            <Badge variant="outline" className="rounded-sm text-[10px]">{m.commodity}</Badge>
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-5 py-3 text-xs text-slate-600 max-w-[120px] truncate">{m.counterparty || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CSV Import */}
        <TabsContent value="import">
          <div className="space-y-4">
            <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-800">Import Movement Data</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="border-2 border-dashed border-slate-200 rounded-sm p-8 text-center">
                  <FileSpreadsheet className="h-10 w-10 mx-auto text-slate-300 stroke-[1.5px]" />
                  <p className="mt-3 text-sm text-slate-600">Upload a CSV file with movement data</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Required columns: trade_id, vessel_name, load_port, discharge_port
                  </p>
                  <p className="text-xs text-slate-400">
                    Optional: movement_id, deal_id, vessel_imo, load_date, discharge_date, quantity_mt, commodity, trader, book, counterparty
                  </p>
                  <div className="mt-4 flex items-center justify-center gap-3">
                    <input
                      id="csv-import-input"
                      type="file"
                      accept=".csv"
                      className="text-sm text-slate-500 file:mr-3 file:py-2 file:px-4 file:rounded-sm file:border file:border-slate-200 file:text-xs file:font-medium file:bg-white file:text-slate-700 hover:file:bg-slate-50"
                      data-testid="csv-import-input"
                    />
                    <Button
                      onClick={handleCSVImport}
                      disabled={importing}
                      data-testid="csv-import-button"
                      className="rounded-sm bg-[#0A2463] hover:bg-[#0A2463]/90 text-white"
                    >
                      {importing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                      {importing ? 'Importing...' : 'Import CSV'}
                    </Button>
                  </div>
                </div>

                {/* Import Result */}
                {importResult && (
                  <div className={`p-4 rounded-sm border ${importResult.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`} data-testid="import-result">
                    <div className="flex items-center gap-2 mb-2">
                      {importResult.success ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-red-600" />
                      )}
                      <span className={`text-sm font-semibold ${importResult.success ? 'text-green-800' : 'text-red-800'}`}>
                        {importResult.success ? 'Import Successful' : 'Import Failed'}
                      </span>
                    </div>
                    {importResult.success ? (
                      <div className="flex gap-4 text-xs">
                        <span className="text-slate-600">Total: <b>{importResult.total_rows}</b></span>
                        <span className="text-green-700">Imported: <b>{importResult.imported}</b></span>
                        <span className="text-amber-600">Duplicates: <b>{importResult.duplicates}</b></span>
                        <span className="text-red-600">Errors: <b>{importResult.errors}</b></span>
                      </div>
                    ) : (
                      <p className="text-xs text-red-700">{importResult.error}</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Import History */}
            {importHistory.length > 0 && (
              <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-slate-800">Import History</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">File</th>
                        <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Rows</th>
                        <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Imported</th>
                        <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Dupes</th>
                        <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importHistory.map(h => (
                        <tr key={h.id} className="border-b border-slate-50">
                          <td className="px-5 py-2 text-xs text-slate-700">{h.filename}</td>
                          <td className="px-5 py-2 text-xs font-mono text-right">{h.total_rows}</td>
                          <td className="px-5 py-2 text-xs font-mono text-right text-green-700">{h.imported}</td>
                          <td className="px-5 py-2 text-xs font-mono text-right text-amber-600">{h.duplicates}</td>
                          <td className="px-5 py-2 text-[10px] text-slate-400 font-mono">
                            {new Date(h.created_at).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
